import { Container, Stack } from '@mui/material'
import { AspectRatio } from '@radix-ui/react-aspect-ratio'
import { PreviewPlayer } from 'app/_components/Player'
import { useRetail } from 'app/_hooks/useRetail'
import _ from 'lodash'
import dynamic from 'next/dynamic'
import { ComponentProps, memo, useEffect } from 'react'
import { isMobile } from 'react-device-detect'
import { GetVideoPreviewModalQuery } from '~/__generated__/graphql'
import { VideoPreviewModalActions } from '~/features/video/hooks/useVideoPreviewModalActions'
import { formatDurationWithUnit } from '~/libs/format'
import { VideoButtonContent } from './VideoButtonContent'
import { VideoDetailContent } from './VideoDetailContent'

const VideoPlayerContentOrigin = dynamic(
  () => import('./VideoPlayerContent').then((modules) => modules.VideoPlayerContent),
  { ssr: false },
)

type ComponentPropsType = Readonly<ComponentProps<typeof VideoPlayerContentOrigin>>
const VideoPlayerContent = memo(
  VideoPlayerContentOrigin,
  (oldValue: ComponentPropsType, newValue: ComponentPropsType) =>
    Object.is(oldValue.onSetup, newValue.onSetup) &&
    Object.is(oldValue.sendTrailInsightsEvent, newValue.sendTrailInsightsEvent) &&
    Object.is(oldValue.src, newValue.src) &&
    // NOTE: プレイリストが更新されるとpreviewVideoが更新されてしまうため、メモ化が効かなくなり動画が再リロードされてしまうため、配列の比較はlodash.isEqualを使う
    _.isEqual(oldValue.vtts, newValue.vtts) &&
    _.isEqual(oldValue.subtitles, newValue.subtitles),
)

interface VideoPreviewModalContentProps {
  videoPreviewModalInfo: NonNullable<GetVideoPreviewModalQuery['originalVideo']>
  videoPreviewModalActions: Omit<VideoPreviewModalActions, 'onClose'>
  playlistId?: string
}

export const VideoPreviewModalContent = ({
  videoPreviewModalInfo,
  videoPreviewModalActions,
  playlistId,
}: VideoPreviewModalContentProps) => {
  const { onSetup, sendTrailInsightsEvent, onClickAddPlaylist, onClickVideoPlay } =
    videoPreviewModalActions
  const isUpComing = videoPreviewModalInfo.status === 'upcoming'

  const { sendRetailEvent } = useRetail()
  useEffect(() => {
    sendRetailEvent({
      eventType: 'detail-page-view',
      productDetails: [
        {
          product: { id: videoPreviewModalInfo.id },
        },
      ],
    })
  }, [videoPreviewModalInfo.id])

  return (
    <Stack spacing={2}>
      <AspectRatio ratio={16 / 9}>
        <Container
          disableGutters
          sx={{
            width: '100%',
            height: '100%',
            borderRadius: !isMobile ? 2 : 0,
            overflow: 'hidden',
          }}
        >
          <PreviewPlayer id={videoPreviewModalInfo.id} />
        </Container>
      </AspectRatio>
      <VideoDetailContent
        id={videoPreviewModalInfo.id}
        title={videoPreviewModalInfo.title ?? ''}
        duration={formatDurationWithUnit(
          videoPreviewModalInfo.duration?.minutes ?? 0,
          videoPreviewModalInfo.duration?.minuteUnitName ?? '',
          videoPreviewModalInfo.duration?.seconds ?? 0,
          videoPreviewModalInfo.duration?.secondUnitName ?? '',
        )}
        description={videoPreviewModalInfo.description ?? ''}
        creator={{
          name: videoPreviewModalInfo.creator?.name ?? '',
          avatar: videoPreviewModalInfo.creator?.avatar ?? '',
        }}
        isUpComing={isUpComing}
        willBePublishedAt={videoPreviewModalInfo.willBePublishedAt ?? ''}
        willBeUnpublishedAt={videoPreviewModalInfo.willBeUnpublishedAt}
      />
      <VideoButtonContent
        videoId={videoPreviewModalInfo.id}
        playlistId={playlistId}
        onClickAddPlaylist={() => onClickAddPlaylist(Number(videoPreviewModalInfo.id))}
        onClickVideoPlay={() => onClickVideoPlay(Number(videoPreviewModalInfo.id))}
        isUpComing={isUpComing}
        videoPreviewModalInfo={videoPreviewModalInfo}
      />
    </Stack>
  )
}
